import Vue from 'vue'
import VueRouter from 'vue-router'
import router from '@/router'
import { analyticsService } from '@/services/analyticsService'

/* navigational routes */

const loginRoute = (to) => ({
  name: 'login',
  query: {
    options: to.query?.options, // forward any login options
    redirect: to.fullPath // includes path, query and hash
  }
})

const redirectRoute = (redirectURI) => {
  const url = new URL(redirectURI, window.location.origin)
  const queryParams = Object.fromEntries(url.searchParams)
  return {
    path: url.pathname,
    query: queryParams
  }
}

const errorRoute = (error) => ({
  name: 'error',
  query: {
    code: error.name || 'SystemError',
    message: error.message
  }
})

/* route guards */

// ensure authentication / authorization
const authGuard = async (to, from) => {
  let nextRoute = null
  const authService = router.app.$auth

  try {
    // wait for store to be loaded
    // await router.app.$store.restored

    // wait for authentication service
    await authService.waitUntilReady()

    // retrieve authentication / authorization status
    console.debug('[router:authGuard]: authenticated=', authService.isAuthenticated())
    console.debug('[router:authGuard]: authorized=', authService.isAuthorized())

    // handle first route visit of current session
    let redirectURI = ''
    if (!authService.isAuthenticated() && from === VueRouter.START_LOCATION) {
      console.debug('[router:authGuard]: Checking for redirect callback after initial app load.')
      // check for redirect promise OR existing session
      const state = await authService.handleRedirect()
      redirectURI = state?.redirectURI || ''
      if (authService.isAuthenticated()) {
        analyticsService.identify(authService.getUserAccount())
      }
      // FIXME: set locale in i18nStore (if state.locale is set and different than current locale)
      // locale = state?.locale
      console.debug('[router:authGuard]: authenticated=', authService.isAuthenticated())
      console.debug('[router:authGuard]: redirectURI=', redirectURI)
    }

    // is route protected?
    const requiresAuth = to.matched.some((route) => route.meta.requiresAuthentication)
    console.debug('[router:authGuard]: Route requiresAuthentication=', requiresAuth)

    if (requiresAuth) {
      if (authService.isAuthenticated()) {
        // authorize the (already authenticated) user
        if (!authService.isAuthorized()) {
          console.debug('[router:authGuard]: Authorizing....')
          const userId = authService.getUserId()
          await authService.authorize(userId) // throws error if not authorized
        }

        // convert analytics identity to email address
        // FIXME: remove at some point (when all users are merged)
        // analyticsService.mergeIdentity(authService.getUserAccount())

        // at this point, user is both authenticated *and* authorized
        console.debug(
          `[router:authGuard]: ${redirectURI ? 'Redirecting....' : 'Proceeding to route...'}`
        )
        nextRoute = redirectURI ? redirectRoute(redirectURI) : null
      } else {
        console.debug('[router:authGuard]: Authenticating....')
        nextRoute = loginRoute(to)
      }
    }
  } catch (error) {
    console.error('[router:authGuard]: An error occured.', error)
    // logout unauthorized users so they can login with a different userId
    if (authService.isAuthenticated()) {
      authService.signOut()
    }
    nextRoute = errorRoute(error)
  }

  return nextRoute
}

// restrict routes to those configured for this tenant
const configGuard = (_to, _from, _next) => {
  // return router.app.$store.state.menuStore.menuRoutes.includes(to.name)
  return true
}

// record route changes for analytics
const pageViewGuard = (to, _from) => {
  const capture = to.matched.some((route) => route.meta.capture !== false)
  if (capture) {
    analyticsService.capture('$pageview', { page_type: 'route', page_name: to.name })
  }
}
const pageLeaveGuard = (to, from) => {
  const capture = from?.matched.some((route) => route.meta.capture !== false)

  if (capture) {
    analyticsService.capture('$pageleave', { page_type: 'route', page_name: from?.name })
  }
}

// saves the scroll position of the "from" page
const scrollGuard = (to, from) => {
  const position = {
    x: window.scrollX,
    y: window.scrollY
  }
  console.debug(`[router:scrollGuard]: scroll position=(${position.x}, ${position.y})`)
  from.meta.scrollPosition = position
}

/* router guards */

// called before navigating to the route
export const beforeEach = async (to, from, next) => {
  console.debug(`[router:beforeEach]: Routing to ${to.fullPath}...`)
  const nextRoute = await authGuard(to, from)
  pageLeaveGuard(to, from)
  nextRoute ? next(nextRoute) : next()
}

// called before component loads, but after in-component guards run
export const beforeResolve = (to, from, next) => {
  const isActive = configGuard(to, from, next)
  isActive ? next() : next({ name: 'error404' })
}

// called just before the "to" component DOM renders
export const afterEach = (to, from) => {
  Vue.nextTick(() => {
    pageViewGuard(to, from)
    if (to.name === 'iplayer') {
      scrollGuard(to, from)
    }
  })
}
